// Draws an Unsigned schedule card onto a canvas at any point of its animation. One renderer serves
// the live preview, the PNG (a frame from the hold) and the MP4 (every frame), so they can't drift.
//
// Layout coordinates are in a 1280x720 space, read off the S30 Canva design (DAG0fVp3jG0) through
// the Canva connector. The animation started as Canva's own, measured frame by frame from that
// design's MP4 export (see tasks/unsigned-graphics.md), and is being tightened where Canva's felt
// slow: names wipe in behind white blocks, the times rise into place, the side players build up out
// of tiles while the centre player slowly fades in, the chevrons shoot in and the crosshair settles.
// The outro takes the opponents, VS and times back out; our names, crests and players stay.

import background from "../../content/unsigned/background.jpg";
import crosshair from "../../content/unsigned/crosshair.png";
import outscale from "../../content/unsigned/outscale.png";
import glerupmedia from "../../content/unsigned/glerupmedia.png";
import powerstats from "../../content/logo.png";
// The league CDN's no-photo placeholder, pre-fitted into the figure frame (head in line with the
// face-normalised photos). Bundled, not fetched: the CDN only serves it for players without a photo.
import silhouetteImg from "../../content/unsigned/silhouette.png";
import { UnsignedMatch, UnsignedPlayer, crestUrl, figureUrl } from "../../services/UnsignedGraphicsService";
import { cardTime } from "./unsignedGraphics";

export const CARD_W = 1280;
export const CARD_H = 720;
export const MAX_ROWS = 4;
export const MAX_PLAYERS = 5;
export const CARD_FONT = "UnsignedOpenSans";

// ---- timeline (seconds, measured on Canva's export) ------------------------------------------

export const DURATION = 20;
/** Everything has settled by ~4.9s. Used for the PNG and as the end of "Play intro". */
export const STILL_T = 5.2;

const ROW_START = 0.35;
const ROW_STAGGER = 0.35;

const BLOCK_GROW = 0.14;   // white block sweeps across the name...
const BLOCK_SHRINK = 0.2;  // ...then pulls away to the right, uncovering it
const OPP_DELAY = 0.12;    // the opponent name follows its own

const VS_PER_LETTER = 0.07;

const TIME_DELAY = 0.15;
const TIME_STAGGER = 0.03;  // per character
const TIME_RISE = 0.45;

const TILES_START = 0.9;
const TILES_SPREAD = 0.55;  // bottom tiles first, heads last
const TILES_JITTER = 0.25;
const TILE_IN = 0.12;
const TILE = 30;            // card px
const GHOST_IN = 0.4;       // side players show faintly before their tiles land
const GHOST_ALPHA = 0.3;
const HERO_START = 0.9;
const HERO_IN = 3.1;        // the centre player's long fade

const DECOR_START = 0.2;
const DECOR_END = 4.9;
const CROSSHAIR_TURN = 20;  // degrees it winds back from

const OUTRO = 0.75;
const OUTRO_START = DURATION - OUTRO;
// Sized so even a 4-row card is fully out by the last frame: 3 x 0.07 + 0.35 x 0.75 + 0.3 x 0.75 < 0.75.
const OUTRO_ROW_STAGGER = 0.07;

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const easeOutCubic = (x: number) => 1 - Math.pow(1 - clamp01(x), 3);
const easeInOutCubic = (x: number) => {
    const p = clamp01(x);
    return p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
};
// A soft overshoot, for things that land (tumbling characters, popping crests).
const easeOutBack = (x: number) => {
    const p = clamp01(x), c1 = 1.4, c3 = c1 + 1;
    return 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2);
};
const progress = (t: number, start: number, length: number) => clamp01((t - start) / length);
const lerp = (a: number, b: number, p: number) => a + (b - a) * p;

/** Deterministic 0..1 noise, so a tile lands at the same moment in every frame and every export. */
const hash01 = (a: number, b: number, c: number) => {
    let h = (a * 374761393 + b * 668265263 + c * 2147483647) | 0;
    h = Math.imul(h ^ (h >>> 13), 1274126177);
    return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

// ---- layout ------------------------------------------------------------------------------------

const ROW_PITCH = 58.7;
const ROWS_CENTRE_Y = 197; // the 2-, 3- and 4-row Canva pages all centre on the crosshair
const NAME_PX = 30.53;
const VS_PX = 54.33;
const TIME_PX = 34.37;
const OWN_NAME = { left: 262, width: 318 };  // centred between its crest and VS
const OPP_NAME = { left: 673, width: 336 };  // left-aligned, up to the opponent crest
const VS_BOX = { left: 590, width: 65 };
const TIME_BOX = { left: 1068, width: 209 };
const CROSSHAIR = { x: 733, y: 47, size: 306 };
// Open Sans' alphabetic baseline sits 0.388em below the middle of a 1em line box.
const BASELINE = 0.388;

// Player figures come from the API face-normalised into an 800x840 frame (eyes at y=140, see
// UnsignedGraphicsController.RenderFigure), drawn at half size. Slots are in fill order - best
// spot first - and mirror the S30 layout: centre in front, raised a little.
const FIG_W = 400;
const FIG_H = 420;
const FIG_EYE = 70;
const CARD_BOTTOM = CARD_H + 2;


interface Slot { x: number; eye: number; k: number; z: number; }
const SLOTS: Record<number, Slot[]> = {
    1: [{ x: 635, eye: 405, k: 1, z: 5 }],
    2: [{ x: 567, eye: 412, k: 1, z: 5 }, { x: 703, eye: 412, k: 1, z: 4 }],
    3: [{ x: 635, eye: 405, k: 1, z: 5 }, { x: 500, eye: 430, k: 0.95, z: 4 }, { x: 770, eye: 430, k: 0.95, z: 4 }],
    4: [{ x: 567, eye: 415, k: 1, z: 5 }, { x: 703, eye: 415, k: 1, z: 4 },
        { x: 432, eye: 445, k: 0.94, z: 3 }, { x: 838, eye: 445, k: 0.94, z: 3 }],
    5: [{ x: 635, eye: 405, k: 1, z: 5 }, { x: 500, eye: 430, k: 0.95, z: 4 }, { x: 778, eye: 428, k: 0.95, z: 4 },
        { x: 365, eye: 458, k: 0.9, z: 3 }, { x: 925, eye: 458, k: 0.9, z: 3 }],
};

// ---- assets ------------------------------------------------------------------------------------

export interface CardData {
    teamId: string;
    teamName: string;
    matches: UnsignedMatch[];   // at most MAX_ROWS
    players: UnsignedPlayer[];  // visible ones, best first
}

// A coarse opacity grid over a figure's 400x420 frame: where the person is, for placing tile edges.
const MASK_W = 50, MASK_H = 105;
type Mask = Uint8Array;

interface Figure { img: HTMLImageElement | null; bottom: number; mask: Mask; }

export interface CardAssets {
    background: HTMLImageElement | null;
    crosshair: HTMLImageElement | null;
    outscale: HTMLImageElement | null;
    glerupmedia: HTMLImageElement | null;
    powerstats: HTMLImageElement | null;
    crests: Map<string, HTMLImageElement | null>;
    figures: Map<string, Figure>; // players whose photo loaded
    silhouette: Figure;           // everyone else
}

// Every image is CORS-clean (bundled, or our CORS-open API), or the canvas couldn't be exported.
const loadImage = (src: string): Promise<HTMLImageElement | null> =>
    new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null); // a missing crest leaves a gap rather than breaking the card
        img.src = src;
    });

/** Coarse opacity grid of whatever `draw` paints into a figure frame. */
const maskOf = (draw: (ctx: Ctx) => void): Mask => {
    const c = document.createElement("canvas");
    c.width = MASK_W;
    c.height = MASK_H;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    const mask = new Uint8Array(MASK_W * MASK_H);
    if (!ctx) return mask.fill(255);
    ctx.scale(MASK_W / FIG_W, MASK_H / FIG_H);
    draw(ctx);
    const data = ctx.getImageData(0, 0, MASK_W, MASK_H).data;
    for (let i = 0; i < mask.length; i++) mask[i] = data[i * 4 + 3];
    return mask;
};

/** Lowest opaque row of a figure, in frame pixels (0..FIG_H). */
const contentBottom = (mask: Mask): number => {
    for (let y = MASK_H - 1; y >= 0; y--)
        for (let x = 0; x < MASK_W; x++)
            if (mask[y * MASK_W + x] > 16) return ((y + 1) / MASK_H) * FIG_H;
    return FIG_H;
};

const opaqueAt = (mask: Mask, fx: number, fy: number) => {
    const x = Math.floor((fx / FIG_W) * MASK_W), y = Math.floor((fy / FIG_H) * MASK_H);
    return x >= 0 && y >= 0 && x < MASK_W && y < MASK_H && mask[y * MASK_W + x] > 64;
};

const figureOf = (img: HTMLImageElement | null): Figure => {
    const mask = img ? maskOf(ctx => ctx.drawImage(img, 0, 0, FIG_W, FIG_H)) : new Uint8Array(MASK_W * MASK_H);
    return { img, bottom: img ? contentBottom(mask) : FIG_H, mask };
};

export const loadCardAssets = async (card: CardData, photoVersion: number): Promise<CardAssets> => {
    const crestIds = Array.from(new Set([card.teamId, ...card.matches.map(m => m.opponent.teamId)].filter(Boolean)));
    const withPhoto = card.players.filter(p => p.hasPhoto);

    const [bg, cross, os, gm, ps, sil, crestImgs, figImgs] = await Promise.all([
        loadImage(background), loadImage(crosshair), loadImage(outscale), loadImage(glerupmedia), loadImage(powerstats),
        loadImage(silhouetteImg),
        Promise.all(crestIds.map(id => loadImage(crestUrl(id)))),
        Promise.all(withPhoto.map(p => loadImage(figureUrl(p.steamId, photoVersion)))),
    ]);

    return {
        background: bg, crosshair: cross, outscale: os, glerupmedia: gm, powerstats: ps,
        crests: new Map(crestIds.map((id, i) => [id, crestImgs[i]])),
        figures: new Map(withPhoto
            .map((p, i) => [p.steamId, figImgs[i]] as const)
            .filter((x): x is readonly [string, HTMLImageElement] => x[1] !== null)
            .map(([id, img]) => [id, figureOf(img)])),
        silhouette: figureOf(sil),
    };
};

// ---- drawing helpers ---------------------------------------------------------------------------

type Ctx = CanvasRenderingContext2D;

const drawImage = (ctx: Ctx, img: HTMLImageElement | null | undefined, x: number, y: number, w: number, h: number) => {
    if (!img) return;
    // object-fit: contain, like the Canva image frames
    const s = Math.min(w / img.naturalWidth, h / img.naturalHeight);
    const dw = img.naturalWidth * s, dh = img.naturalHeight * s;
    ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
};

const setFont = (ctx: Ctx, px: number) => { ctx.font = `700 ${px}px ${CARD_FONT}`; };

/** Largest font size <= base at which `text` fits in `maxWidth`. */
const fitPx = (ctx: Ctx, text: string, base: number, maxWidth: number): number => {
    setFont(ctx, base);
    const width = ctx.measureText(text).width;
    return width <= maxWidth ? base : Math.floor((base * maxWidth / width) * 10) / 10;
};

/**
 * Canva's "block" text animation: a white block sweeps in from the left, then pulls away to the
 * right uncovering the text. With `outStart` set, the outro plays it backwards: the block covers
 * the text again from the right, then shrinks away to the left.
 */
const blockText = (ctx: Ctx, text: string, px: number, x: number, midY: number,
    t: number, start: number, outStart: number | null) => {
    setFont(ctx, px);
    const w = ctx.measureText(text).width;
    const barTop = midY - px * 0.52;
    const barH = px * 1.04;

    const grow = easeInOutCubic(progress(t, start, BLOCK_GROW));
    const shrink = easeInOutCubic(progress(t, start + BLOCK_GROW, BLOCK_SHRINK));
    const cover = outStart === null ? 0 : easeInOutCubic(progress(t, outStart, BLOCK_SHRINK));
    const leave = outStart === null ? 0 : easeInOutCubic(progress(t, outStart + BLOCK_SHRINK, BLOCK_GROW));

    if (grow <= 0) return;
    ctx.fillStyle = "#fff";

    if (shrink > 0 && leave === 0) ctx.fillText(text, x, midY + px * BASELINE);

    if (shrink === 0) ctx.fillRect(x, barTop, w * grow, barH);
    else if (shrink < 1) ctx.fillRect(x + w * shrink, barTop, w * (1 - shrink), barH);
    else if (cover > 0 && leave === 0) ctx.fillRect(x + w * (1 - cover), barTop, w * cover, barH);
    else if (leave > 0 && leave < 1) ctx.fillRect(x, barTop, w * (1 - leave), barH);
};

/**
 * The times rise into place: each character slides up out of a mask along its own line, a few
 * hundredths of a second after the one before, so the whole time lands as one quick motion. (Canva
 * tumbled them in upside down over ~1.6s, which read as slow and fussy.) The outro lifts them out
 * of the top of the same mask.
 */
const riseText = (ctx: Ctx, text: string, px: number, left: number, midY: number,
    t: number, start: number, outStart: number) => {
    setFont(ctx, px);
    const chars = Array.from(text);
    const width = ctx.measureText(text).width;
    const outStagger = (OUTRO * 0.35) / chars.length;

    ctx.save();
    ctx.beginPath();
    ctx.rect(left - 6, midY - px * 0.62, width + 12, px * 1.14); // cap height + a little room
    ctx.clip();
    let x = left;
    chars.forEach((ch, j) => {
        const w = ctx.measureText(ch).width;
        const pIn = progress(t, start + j * TIME_STAGGER, TIME_RISE);
        const pOut = easeInCubic(progress(t, outStart + j * outStagger, OUTRO * 0.3));
        if (pIn > 0 && pOut < 1 && ch !== " ") {
            const dy = (1 - easeOutExpo(pIn)) * px * 1.1 - pOut * px * 1.1;
            ctx.globalAlpha = clamp01(pIn * 3) * (1 - pOut);
            ctx.fillText(ch, x, midY + dy + px * BASELINE);
        }
        x += w;
    });
    ctx.restore();
};
/**
 * The "Arrow Direction Pointer" pair from the Canva cards: a bold outlined chevron band with a thin
 * chevron inside it. Canva drifted it in over ~4.7s; here it shoots in off the left edge and
 * settles (expo ease-out), the inner chevron a beat behind the band, with a short motion trail
 * while it's fast so the speed reads at 30fps. It leaves the same way in the outro.
 */
const CHEVRON_START = 0.15;
const CHEVRON_IN = 0.55;
const CHEVRON_FOLLOW = 0.08;
const CHEVRON_TRAVEL = 240;
const CHEVRON_OUT = 0.35;
const CHEVRON_TRAIL: [number, number][] = [[0.03, 0.28], [0.06, 0.12]]; // [seconds behind, alpha]

const easeOutExpo = (x: number) => { const p = clamp01(x); return p === 1 ? 1 : 1 - Math.pow(2, -10 * p); };
const easeInCubic = (x: number) => Math.pow(clamp01(x), 3);

/** Horizontal offset and opacity of one chevron part at `t`. */
const chevronAt = (t: number, delay: number) => {
    const pIn = progress(t, CHEVRON_START + delay, CHEVRON_IN);
    const pOut = easeInCubic(progress(t, DURATION - CHEVRON_OUT - 0.05 - delay, CHEVRON_OUT));
    return {
        x: -(1 - easeOutExpo(pIn)) * CHEVRON_TRAVEL - pOut * CHEVRON_TRAVEL,
        alpha: clamp01(pIn * 5) * (1 - pOut),
    };
};

const strokeBand = (ctx: Ctx) => {
    ctx.lineWidth = 5.5;
    ctx.beginPath();
    [[8, 57], [52, 57], [179, 197], [52, 338], [8, 338], [133, 197]]
        .forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
    ctx.stroke();
};

const strokeInner = (ctx: Ctx) => {
    ctx.lineWidth = 4.5;
    ctx.beginPath();
    ctx.moveTo(-6, 128);
    ctx.lineTo(62, 197);
    ctx.lineTo(-6, 266);
    ctx.stroke();
};

const drawChevrons = (ctx: Ctx, t: number) => {
    const draw = (stroke: (ctx: Ctx) => void, x: number, alpha: number) => {
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = "#fff";
        ctx.lineJoin = "round";
        ctx.translate(x, 0);
        stroke(ctx);
        ctx.restore();
    };
    for (const [stroke, delay] of [[strokeBand, 0], [strokeInner, CHEVRON_FOLLOW]] as const) {
        const now = chevronAt(t, delay);
        // Trail copies only while it's actually moving; at rest they'd sit exactly underneath.
        for (const [behind, alpha] of CHEVRON_TRAIL) {
            const then = chevronAt(t - behind, delay);
            if (Math.abs(now.x - then.x) >= 2 && then.alpha > 0) draw(stroke, then.x, then.alpha * alpha);
        }
        if (now.alpha > 0) draw(stroke, now.x, now.alpha);
    }
};
const drawCrosshair = (ctx: Ctx, t: number, img: HTMLImageElement | null) => {
    if (!img) return;
    const settle = easeOutCubic(progress(t, DECOR_START, DECOR_END - DECOR_START));
    const out = easeInOutCubic(progress(t, OUTRO_START + 0.35, OUTRO - 0.35));
    const alpha = easeOutCubic(progress(t, DECOR_START, 0.35));
    if (alpha <= 0) return;
    const cx = CROSSHAIR.x + CROSSHAIR.size / 2, cy = CROSSHAIR.y + CROSSHAIR.size / 2;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(cx, cy);
    ctx.rotate(((1 - settle) * CROSSHAIR_TURN - out * CROSSHAIR_TURN) * Math.PI / 180);
    drawImage(ctx, img, -CROSSHAIR.size / 2, -CROSSHAIR.size / 2, CROSSHAIR.size, CROSSHAIR.size);
    ctx.restore();
};

/**
 * League cut-outs stop at the chest or waist, which would leave a torso floating above the card's
 * bottom edge - so a short figure is lowered until it runs off the bottom. It is never scaled: every
 * face on a card stays the same size (scaling short cut-outs up made those players look too big).
 */
const placeFigure = (slot: Slot, bottom: number) => {
    const drop = Math.max(0, CARD_BOTTOM - slot.eye - (bottom - FIG_EYE) * slot.k);
    return { k: slot.k, left: slot.x - (FIG_W / 2) * slot.k, top: slot.eye + drop - FIG_EYE * slot.k };
};

const drawFigureAt = (ctx: Ctx, img: HTMLImageElement | null, left: number, top: number, k: number) => {
    ctx.save();
    ctx.translate(left, top);
    ctx.scale(k, k);
    if (img) ctx.drawImage(img, 0, 0, FIG_W, FIG_H);
    ctx.restore();
};

/**
 * Canva's tile reveal for the side players: a faint ghost first, then the figure lands in square
 * tiles - lower tiles first, heads last, with some scatter - each flashing a white edge as it lands.
 */
const drawTiled = (ctx: Ctx, t: number, seed: number, img: HTMLImageElement | null, mask: Mask,
    left: number, top: number, k: number) => {
    const w = FIG_W * k, h = FIG_H * k;
    const ghost = easeOutCubic(progress(t, TILES_START - 0.1, GHOST_IN)) * GHOST_ALPHA;
    const allIn = TILES_START + TILES_SPREAD + TILES_JITTER + TILE_IN;
    if (t >= allIn) {
        drawFigureAt(ctx, img, left, top, k);
        return;
    }

    if (ghost > 0) {
        ctx.save();
        ctx.globalAlpha *= ghost;
        drawFigureAt(ctx, img, left, top, k);
        ctx.restore();
    }

    const cols = Math.ceil(w / TILE), rows = Math.ceil(h / TILE);
    // Order over the part that's actually on the card: the frame runs off the bottom edge.
    const visibleRows = Math.max(1, Math.ceil((Math.min(CARD_H, top + h) - top) / TILE));
    const landed: [number, number, number][] = [];
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
            const depth = Math.min(1, r / Math.max(1, visibleRows - 1)); // 0 = head, 1 = card bottom
            const at = TILES_START + (1 - depth) * TILES_SPREAD + hash01(seed, c, r) * TILES_JITTER;
            const p = progress(t, at, TILE_IN);
            if (p > 0) landed.push([c, r, p]);
        }
    }
    if (landed.length === 0) return;

    ctx.save();
    ctx.beginPath();
    for (const [c, r] of landed) ctx.rect(left + c * TILE, top + r * TILE, TILE, TILE);
    ctx.clip();
    drawFigureAt(ctx, img, left, top, k);
    ctx.restore();

    // The white edge of each tile as it lands - only on the person, never on the empty frame.
    ctx.save();
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1;
    for (const [c, r, p] of landed) {
        if (p >= 1 || !opaqueAt(mask, (c + 0.5) * TILE / k, (r + 0.5) * TILE / k)) continue;
        ctx.globalAlpha = 0.55 * (1 - p);
        ctx.strokeRect(left + c * TILE + 0.5, top + r * TILE + 0.5, TILE - 1, TILE - 1);
    }
    ctx.restore();
};

const drawPlayers = (ctx: Ctx, t: number, card: CardData, assets: CardAssets) => {
    // No photo, or it failed to load: the league silhouette, placed and animated like a photo.
    const withFigures = card.players.slice(0, MAX_PLAYERS)
        .map(p => ({ p, fig: (p.hasPhoto && assets.figures.get(p.steamId)) || assets.silhouette }));
    const slots = SLOTS[withFigures.length] ?? [];
    // Longest cut-outs take the best (centre, highest) slots: the centre stands highest, so it needs
    // the most body below the face. Short ones go outside, where the slots sit lower anyway and the
    // drop that reaches the bottom edge barely shows.
    const placed = withFigures
        .map((x, order) => ({ ...x, order }))
        .sort((a, b) => b.fig.bottom - a.fig.bottom || a.order - b.order)
        .map((x, i) => ({ ...x, i, slot: slots[i] }))
        .filter(x => x.slot)
        .sort((a, b) => a.slot.z - b.slot.z); // back to front

    for (const { fig, i, slot } of placed) {
        const img = fig.img;
        const { k, left, top } = placeFigure(slot, fig.bottom);

        if (i === 0 && withFigures.length !== 2 && withFigures.length !== 4) {
            // The centre player - slot 0 when there is a centre - takes the long fade.
            const a = easeInOutCubic(progress(t, HERO_START, HERO_IN));
            if (a <= 0) continue;
            ctx.save();
            ctx.globalAlpha = a;
            drawFigureAt(ctx, img, left, top, k);
            ctx.restore();
        } else {
            drawTiled(ctx, t, i + 1, img, fig.mask, left, top, k);
        }
    }
};

// ---- the card ----------------------------------------------------------------------------------

/** Draw the whole card at time `t` (seconds). The context must be in 1280x720 card space. */
export const drawCard = (ctx: Ctx, t: number, card: CardData, assets: CardAssets) => {
    ctx.save();
    ctx.fillStyle = "#0b0d24";
    ctx.fillRect(0, 0, CARD_W, CARD_H);
    if (assets.background) ctx.drawImage(assets.background, 0, 0, CARD_W, CARD_H);

    drawChevrons(ctx, t);
    drawCrosshair(ctx, t, assets.crosshair);

    const rows = card.matches.slice(0, MAX_ROWS);
    const firstRowTop = ROWS_CENTRE_Y - (rows.length * ROW_PITCH) / 2;
    const ownName = card.teamName.toUpperCase();
    const ownPx = fitPx(ctx, ownName, NAME_PX, OWN_NAME.width);
    setFont(ctx, ownPx);
    const ownX = OWN_NAME.left + (OWN_NAME.width - ctx.measureText(ownName).width) / 2;

    ctx.textBaseline = "alphabetic";
    rows.forEach((m, i) => {
        const top = firstRowTop + i * ROW_PITCH;
        const mid = top + ROW_PITCH / 2;
        const start = ROW_START + i * ROW_STAGGER;
        const outStart = OUTRO_START + i * OUTRO_ROW_STAGGER;

        // Crests pop in with their row and stay through the outro.
        const pop = progress(t, start, 0.3);
        if (pop > 0) {
            const s = lerp(0.7, 1, easeOutBack(pop));
            ctx.save();
            ctx.globalAlpha = easeOutCubic(pop);
            const own = { x: 189, y: top, size: 58.7 };
            ctx.translate(own.x + own.size / 2, own.y + own.size / 2);
            ctx.scale(s, s);
            drawImage(ctx, assets.crests.get(card.teamId), -own.size / 2, -own.size / 2, own.size, own.size);
            ctx.restore();
            if (m.opponent.teamId) {
                ctx.save();
                ctx.globalAlpha = easeOutCubic(pop);
                ctx.translate(1017 + 30.5, top + 3 + 30.5);
                ctx.scale(s, s);
                drawImage(ctx, assets.crests.get(m.opponent.teamId), -30.5, -30.5, 61, 61);
                ctx.restore();
            }
        }

        // VS types in a letter at a time and fades in the outro.
        setFont(ctx, VS_PX);
        const vsLeft = VS_BOX.left + (VS_BOX.width - ctx.measureText("VS").width) / 2;
        const vsOut = 1 - easeInOutCubic(progress(t, outStart, 0.35));
        let vx = vsLeft;
        ["V", "S"].forEach((ch, j) => {
            const a = easeOutCubic(progress(t, start + j * VS_PER_LETTER, 0.12)) * vsOut;
            if (a > 0) {
                ctx.save();
                ctx.globalAlpha = a;
                ctx.fillStyle = "#fff";
                ctx.fillText(ch, vx, mid + 1 + VS_PX * BASELINE);
                ctx.restore();
            }
            vx += ctx.measureText(ch).width;
        });

        // Our name stays for the loop; the opponent's is taken back out.
        blockText(ctx, ownName, ownPx, ownX, mid, t, start, null);
        const opp = m.opponent.teamName.toUpperCase();
        blockText(ctx, opp, fitPx(ctx, opp, NAME_PX, OPP_NAME.width), OPP_NAME.left, mid, t, start + OPP_DELAY, outStart);

        const time = cardTime(m.matchTimeUtc);
        setFont(ctx, TIME_PX);
        const timeLeft = TIME_BOX.left + (TIME_BOX.width - ctx.measureText(time).width) / 2;
        ctx.fillStyle = "#fff";
        riseText(ctx, time, TIME_PX, timeLeft, mid + 1, t, start + TIME_DELAY, outStart);
    });

    drawPlayers(ctx, t, card, assets);

    // Sponsors are there from the first frame, as in the Canva videos.
    drawImage(ctx, assets.glerupmedia, 92, 548, 71, 78);
    drawImage(ctx, assets.outscale, 53, 652, 161, 35);
    drawImage(ctx, assets.powerstats, 1128, 597, 101, 101);
    ctx.restore();
};

/** Draw the card at `t` on a canvas of `scale` x the card size. */
export const renderToCanvas = (canvas: HTMLCanvasElement, scale: number, t: number, card: CardData, assets: CardAssets) => {
    const w = Math.round(CARD_W * scale), h = Math.round(CARD_H * scale);
    // Resizing clears and reallocates the canvas - only do it when the size actually changes.
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.imageSmoothingQuality = "high";
    drawCard(ctx, t, card, assets);
};
